import { Box, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import __ from "web/Layout/Translations";

import Breadcrumbs from "web/Components/Common/Breadcrumbs";

import useGetAvailableUserBenefits from "web/hooks/useGetAvailableUserBenefits";

import prepareSelectionWindowData from "web/utils/page/customer/prepareSelectionWindowData";
import getBenefitKeys from "web/utils/system/essentials/getBenefitKeys";

import urls from "web/constants/urls";

import { ISubscriptionItemMs } from "web/types/Subscription";

import { useAppContext } from "web/context/app";
import { useGetEmployeeConfigurationQuery } from "web/features/employee/employeeApiSlice";
import { getFilteredBenefitGroups } from "web/features/subscriptions/benefitGroupsThunks";
import useDataCachedIdsFacetsStats from "web/features/useDataCached/useDataCachedIdsFacetsStats";
import useDataCachedProductsInList from "web/features/useDataCached/useDataCachedProductsInList";

import SubscriptionBoxMUI from "./subscriptionBoxMUI";

const SubscriptionsGroupsMUI = () => {
  const { isMobile } = useAppContext();
  const { subscriptions_category: subscriptionsCategory } = useSelector(
    (state) => state.app.storeConfig
  );
  const { config } = useSelector((state) => state.subscriptionQuote);
  const dispatch = useDispatch();

  const { data: employeeConfig } = useGetEmployeeConfigurationQuery();
  const { showSubscriptionsInListing, showSubscriptionsInListingUMS } =
    employeeConfig || {};

  const options = useMemo(() => {
    return {
      variables: {
        categoryId: [+subscriptionsCategory],
        facet: false,
      },
      context: {
        clientName: "ms",
      },
      fetchPolicy: "cache-and-network",
    };
  }, [subscriptionsCategory]);
  const {
    loading: categoryLoading,
    error: categoryError,
    data: categoryData,
  } = useDataCachedIdsFacetsStats({
    options: JSON.stringify(options),
  });

  const subscriptionItems = categoryData?.subscription_items ?? [];

  useEffect(() => {
    if (categoryData?.subscription_items) {
      const subscriptionsSku =
        (categoryData?.subscription_items as ISubscriptionItemMs[])?.map(
          (el) => el.sku
        ) || [];
      dispatch(getFilteredBenefitGroups({ skuArray: subscriptionsSku }));
    }
  }, [categoryData]);

  const dataFiltered = useSelector((state) => state.benefitGroups);

  const isLoadingFilteredSub = dataFiltered?.isLoading;

  const filteredSubSkuArray = getBenefitKeys(dataFiltered ?? null);

  const validSubscriptionIds = subscriptionItems?.reduce(
    (res: number[], curr: ISubscriptionItemMs) => {
      if (filteredSubSkuArray.includes(curr.sku)) {
        res.push(+curr.id);
      }
      return res;
    },
    []
  );

  const { loading, error, data } = useDataCachedProductsInList({
    ids: validSubscriptionIds,
  });

  const benefits = useGetAvailableUserBenefits(data);
  const selectionWindowData = prepareSelectionWindowData({
    ...config,
    showSubscriptionsInListing,
    showSubscriptionsInListingUMS,
  });

  const breadcrumbsDefault = [
    {
      label: __("Strona główna"),
      url: urls.home,
    },
    {
      label: __("Abonamenty"),
      url: urls.subscriptions,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {!isMobile && (
        <Box>
          <Breadcrumbs breadcrumbs={breadcrumbsDefault} />
        </Box>
      )}
      <Typography sx={{ textAlign: "center", mb: 2 }} variant="h1">
        {__("Abonamenty")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "center", md: "stretch" },
          justifyContent: "center",
          gap: 3,
        }}
      >
        {selectionWindowData &&
          selectionWindowData.map((windowData, index) => {
            return windowData.isGroupAvailable && windowData.showOnListing ? (
              <SubscriptionBoxMUI
                data={windowData}
                listingData={benefits[index]}
                isLoading={categoryLoading || loading || isLoadingFilteredSub}
                hasError={!!(categoryError || error)}
              />
            ) : null;
          })}
      </Box>
    </Box>
  );
};

export default SubscriptionsGroupsMUI;
